import { DoctorType, QueryFactory } from '@lmt/shared-types';
import { ResourceDto, ResourceType } from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import {
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import React, { FC, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import { getDoctorFullName } from '../../doctors/doctor-form/common/common';
import { HookFormDropDownInput } from '../../common/ui-kit/inputs/dropdown/DropDownInput';

interface ResourceData {
  resourceName: string;
  doctorId?: number | null;
}

interface CreateOrEditResourceProps {
  resourceType: ResourceType;
  resourceToEdit?: ResourceDto;
}

const nameof = nameofFactory<ResourceData>();

function getTranslationScope(type: ResourceType) {
  switch (type) {
    case ResourceType.Places:
      return 'resources.place.form';
    case ResourceType.SpecialConditions:
      return 'resources.special_conditions.form';
    case ResourceType.Doctors:
      return 'resources.doctors.form';
    default:
      assertNever(type);
  }
}

export const CreateOrEditResource: FC<
  FormComponentProps & CreateOrEditResourceProps
> = (props) => {
  const translationScope = getTranslationScope(props.resourceType);
  const i18next = useScopedTranslation(translationScope);
  const dispatch = useUniDispatch();
  const modals = useModal();
  const queryCache = useQueryClient();
  const doctorsQuery = QueryFactory.DoctorQuery.useGetDoctorsQuery({
    doctorType: DoctorType.Clinic,
    all: true,
    isUsedAsResource: !!props.resourceToEdit,
    doctorIds: props.resourceToEdit?.doctorId
      ? [props.resourceToEdit.doctorId]
      : null,
  });
  const { formType, formId, tryCompleteForm } = props;

  const { register, reset, handleSubmit, formState, setError, control } =
    useForm<ResourceData>({
      shouldFocusError: true,
      criteriaMode: 'all',
    });

  useEffect(() => {
    if (props.resourceToEdit) {
      setDefaultValues({
        resourceName: props.resourceToEdit.name,
        doctorId: props.resourceToEdit.doctorId,
      });
    }
  }, []);

  function setDefaultValues(resource: ResourceData) {
    reset(resource);
  }

  const submitForm = useErrorHandler(
    useCallback(
      async (data: ResourceData) => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            if (props.resourceToEdit) {
              await ClientFactory.ResourceClient.editResource(
                props.resourceToEdit.id,
                {
                  name: data.resourceName,
                },
              );
            } else {
              await ClientFactory.ResourceClient.createResource({
                name: data.resourceName,
                doctorId:
                  props.resourceType == ResourceType.Doctors
                    ? data.doctorId
                    : null,
                type: props.resourceType!,
              });
            }
            await queryCache.invalidateQueries({
              queryKey: QueryFactory.ResourceQuery.getResourcesQueryKey(),
            });
            if (data.doctorId) {
              await queryCache.invalidateQueries({
                queryKey: QueryFactory.DoctorQuery.getDoctorsQueryKey(),
              });
              await queryCache.invalidateQueries({
                queryKey: QueryFactory.DoctorQuery.getDoctorQueryKey(
                  data.doctorId,
                ),
              });
            }
          });
        } catch (err) {
          const errorDetails = handleSubmitFormError<ResourceData>(
            err,
            setError,
          );
          if (errorDetails.overallServerError) {
            await modals.showError({ text: errorDetails.overallServerError });
          }
        }
      },
      [formType, formId, tryCompleteForm],
    ),
    setError,
  );

  return (
    <form
      className={styles.autoSizedContainer}
      onSubmit={handleSubmit(submitForm.handler)}
      data-test-id="editResourcePopup"
    >
      <div className={styles.dataContainer}>
        <Field
          className={styles.dataField}
          title={i18next.t(`name_field`)}
          required={true}
        >
          <Input
            {...register(nameof('resourceName'), requiredRule())}
            errorText={formState.errors.resourceName?.message}
            autoFocus={true}
            autoComplete="off"
          />
        </Field>
        {props.resourceType === ResourceType.Doctors && (
          <Field
            className={styles.field}
            title={i18next.t('doctor')}
            required={true}
          >
            <HookFormDropDownInput
              {...register(
                nameof('doctorId'),
                requiredRule(props.resourceType === ResourceType.Doctors),
              )}
              disabled={!!props.resourceToEdit}
              required
              control={control}
              name={'doctorId'}
              enableSearch={true}
              useVirtualization={true}
              idFunction={'id'}
              getOptionLabel={getDoctorFullName}
              useIdFunctionAsValue={true}
              testId="patient-doctorId"
              options={doctorsQuery.data?.data ?? []}
              errorText={formState.errors.doctorId?.message}
            />
          </Field>
        )}
      </div>
      <div className={styles.submitContainer}>
        <SubmitButton
          submitting={formState.isSubmitting}
          title={
            props.resourceToEdit
              ? i18next.t(`save_resource`)
              : i18next.t(`create_resource`)
          }
        />
      </div>
    </form>
  );
};
